/**
 * Delete Task Warning Modal
 * Story 292: Delete tasks from web UI with coverage warning
 *
 * Shows warning when deleting a task would break coverage (i.e., the task
 * is the only one implementing certain design capabilities).
 */

import { useEffect } from 'react';
import './DeleteTaskWarningModal.css';

interface AffectedCapability {
  id: string;
  title: string;
}

interface DeleteTaskWarningModalProps {
  isOpen: boolean;
  affectedCapabilities: AffectedCapability[];
  onCancel: () => void;
  onDeleteAnyway: () => void;
  onNavigateToCapability: (capabilityId: string) => void;
}

export function DeleteTaskWarningModal({
  isOpen,
  affectedCapabilities,
  onCancel,
  onDeleteAnyway,
  onNavigateToCapability,
}: DeleteTaskWarningModalProps) {
  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleCapabilityClick = (capabilityId: string) => {
    onCancel(); // Close modal first
    onNavigateToCapability(capabilityId);
  };

  return (
    <div className="delete-task-warning__backdrop" onClick={onCancel}>
      <div
        className="delete-task-warning__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-warning-title"
      >
        <h2 id="delete-warning-title" className="delete-task-warning__title">
          This will break coverage
        </h2>

        <p className="delete-task-warning__message">
          This task is the only one implementing these capabilities:
        </p>

        <ul className="delete-task-warning__list">
          {affectedCapabilities.map((cap) => (
            <li key={cap.id} className="delete-task-warning__item">
              <button
                className="delete-task-warning__link"
                onClick={() => handleCapabilityClick(cap.id)}
              >
                {cap.title || cap.id}
              </button>
            </li>
          ))}
        </ul>

        <div className="delete-task-warning__actions">
          <button
            className="delete-task-warning__btn delete-task-warning__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="delete-task-warning__btn delete-task-warning__btn--delete"
            onClick={onDeleteAnyway}
          >
            Delete Anyway
          </button>
        </div>
      </div>
    </div>
  );
}
